import { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';

interface PlantAnalysis {
  hasPlant: boolean;
  status: 'healthy' | 'needs attention' | 'unhealthy' | 'no plants detected';
  description: string;
  confidence: number;
  plant_id?: string;
  plant_species?: string;
  plant_common_name?: string;
  plant_confidence?: number;
  care_recommendations?: string[];
  disease_detected?: string;
}

interface PlantAnalysisDisplayProps {
  photoUrl: string;
  deviceId: string;
  accountId?: string;
  compact?: boolean;
}

export const PlantAnalysisDisplay: React.FC<PlantAnalysisDisplayProps> = ({
  photoUrl,
  deviceId,
  accountId,
  compact = false
}) => {
  const [analysis, setAnalysis] = useState<PlantAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(null);
  const [refreshCount, setRefreshCount] = useState(0);
  const [isAutoRefreshing, setIsAutoRefreshing] = useState(false);
  const { getToken } = useAuth();

  const API_BASE = process.env.REACT_APP_API_BASE || 'https://dev.api.herba.green/v1';

  const fetchAnalysis = async () => {
    if (!photoUrl || !deviceId) return;

    setLoading(true);
    setError(null);

    try {
      // Get authentication token
      const token = await getToken();
      
      // Log API endpoint and photo URL for debugging
      console.log('🔍 Fetching analysis:', {
        apiUrl: `${API_BASE}/devices/${deviceId}/analyses?limit=100`,
        photoUrl,
        deviceId,
        apiBase: API_BASE
      });
      
      // Fetch analysis from aiscan collection for this device  
      const response = await fetch(`${API_BASE}/devices/${deviceId}/analyses?limit=100`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        
        console.log('📊 API Response:', {
          totalAnalyses: data.analyses?.length || 0,
          lookingForUrl: photoUrl,
          firstFewUrls: data.analyses?.slice(0, 3).map((a: any) => a.photo_url)
        });
        
        // Find analysis that matches this specific photo URL
        const existingAnalysis = data.analyses?.find((a: any) => a.photo_url === photoUrl);
        
        if (existingAnalysis) {
          // Clear auto-refresh interval when analysis is found
          if (refreshInterval) {
            clearInterval(refreshInterval);
            setRefreshInterval(null);
            setIsAutoRefreshing(false);
          }
          
          setAnalysis({
            hasPlant: existingAnalysis.status !== 'no plants detected' && existingAnalysis.status !== null,
            status: existingAnalysis.status || 'no plants detected',
            description: existingAnalysis.description,
            confidence: existingAnalysis.confidence,
            plant_id: existingAnalysis.plant_id,
            plant_species: existingAnalysis.plant_species,
            plant_common_name: existingAnalysis.plant_common_name,
            plant_confidence: existingAnalysis.plant_confidence,
            care_recommendations: existingAnalysis.care_recommendations,
            disease_detected: existingAnalysis.disease_detected
          });
          
          // Explicitly set error to null and loading to false to stop any other refresh attempts
          setError(null);
          setLoading(false);
          return true; // Return true to indicate analysis was found
        } else {
          // Only set error if we're not auto-refreshing
          if (!isAutoRefreshing) {
            // Check if there are any analyses at all
            if (data.analyses && data.analyses.length > 0) {
              setError('This photo hasn\'t been analyzed yet. Analysis may still be processing...');
            } else {
              setError('No plant analyses found for this device yet.');
            }
          }
          return false; // Return false to indicate analysis not found
        }
      } else {
        setError(`API error: ${response.status}`);
        return true; // Return true to stop auto-refresh on error
      }
    } catch (err) {
      setError('Failed to load analysis');
      return true; // Return true to stop auto-refresh on error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Reset previous analysis when URL changes
    setAnalysis(null);
    
    // Clear any existing interval
    if (refreshInterval) {
      clearInterval(refreshInterval);
      setRefreshInterval(null);
    }

    const startAnalysisCheck = async () => {
      const found = await fetchAnalysis();
      
      // Only start auto-refresh if analysis wasn't found and no interval is running
      if (!found && !refreshInterval) {
        setRefreshCount(0);
        setIsAutoRefreshing(true);
        setError(null); // Clear any existing errors when starting auto-refresh
        
        const interval = setInterval(async () => {
          setRefreshCount(prev => {
            const newCount = prev + 1;
            
            // Stop after 30 attempts (60 seconds)
            if (newCount >= 30) {
              clearInterval(interval);
              setRefreshInterval(null);
              setIsAutoRefreshing(false);
              setError('Analysis is taking longer than expected. Please refresh the page to check again.');
              return newCount;
            }
            
            // Fetch analysis and stop interval if found
            fetchAnalysis().then(found => {
              if (found) {
                clearInterval(interval);
                setRefreshInterval(null);
                setIsAutoRefreshing(false);
              }
            });
            
            return newCount;
          });
        }, 2000);
        
        setRefreshInterval(interval);
      }
    };

    startAnalysisCheck();
    
    // Cleanup interval on unmount or when dependencies change
    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
        setRefreshInterval(null);
      }
    };
  }, [photoUrl, deviceId]);

  if (loading || isAutoRefreshing) {
    return (
      <div className="flex items-center space-x-2 text-xs text-gray-500">
        <div className="animate-spin rounded-full h-3 w-3 border-b border-blue-500"></div>
        <span>{isAutoRefreshing ? `Analyzing... (attempt ${refreshCount}/30)` : 'Analyzing...'}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 p-3 rounded-lg">
        <p className="text-red-700 text-sm">{error}</p>
        <p className="text-red-500 text-xs mt-1">Photo URL: {photoUrl}</p>
        <p className="text-red-500 text-xs">Device ID: {deviceId}</p>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="bg-gray-50 p-3 rounded-lg">
        <p className="text-gray-500 text-sm">No analysis data available for this image</p>
        <p className="text-gray-400 text-xs mt-1">Photo URL: {photoUrl}</p>
      </div>
    );
  }

  const getStatusColor = () => {
    if (!analysis.hasPlant) return 'text-gray-500';
    switch (analysis.status) {
      case 'healthy': return 'text-green-600';
      case 'unhealthy': return 'text-red-600';
      case 'needs attention': return 'text-yellow-600';
      default: return 'text-gray-500' ;
    }
  };

  const getStatusIcon = () => {
    if (!analysis.hasPlant || analysis.status === 'no plants detected' || analysis.status === null) return '🔎';
    switch (analysis.status) {
      case 'healthy': return '🌿';
      case 'unhealthy': return '🚨';
      case 'needs attention': return '⚠️';
      default: return '❓';
    }
  };

  if (compact) {
    return (
      <div className="text-xs space-y-1">
        <div className={`flex items-center space-x-1 ${getStatusColor()}`}>
          <span>{getStatusIcon()}</span>
          <span className="font-medium">{analysis.hasPlant ? analysis.status : 'No plants'}</span>
          <span className="text-gray-400">({analysis.confidence}%)</span>
        </div>
        {analysis.plant_common_name && (
          <div className="text-gray-600">{analysis.plant_common_name}</div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-gray-50 p-3 rounded-lg space-y-2 text-sm">
      <div className={`flex items-center space-x-2 ${getStatusColor()}`}>
        <span className="text-4xl mr-3">{getStatusIcon()}</span>
        <div>
          <div className="text-lg pt-3 font-bold">
            {analysis.hasPlant && analysis.status !== 'no plants detected' && analysis.status !== null ? `Plant: ${analysis.status}` : 'No plants detected'}
          </div>
          <div className="text-xs  pb-3 text-gray-500">
            {analysis.confidence}% confidence
          </div>
        </div>
      </div>
      {analysis.plant_common_name && (
        <div className="text-xs pb-3 font-bold">
          <span className="font-medium text-gray-700">Species:</span> {analysis.plant_common_name}
        </div>
      )}

      <p className="text-gray-700 text-sm">
        {analysis.description}
      </p>

      {analysis.disease_detected && (
        <div className="text-sm text-red-600">
          <span className="font-medium">Issue:</span> {analysis.disease_detected}
        </div>
      )}

      {/* {analysis.care_recommendations && analysis.care_recommendations.length > 0 && (
        <div className="text-xs">
          <div className="font-medium text-gray-700 mb-1">Care tips:</div>
          <ul className="space-y-1 text-gray-600">
            {analysis.care_recommendations.slice(0, 3).map((tip, index) => (
              <li key={index} className="flex items-start">
                <span className="mr-1">•</span>
                <span>{tip}</span>
              </li>
            ))}
          </ul>
          {analysis.care_recommendations.length > 3 && (
            <div className="text-gray-500 mt-1">
              +{analysis.care_recommendations.length - 3} more tips
            </div>
          )}
        </div>
      )} */}
    </div>
  );
};